import React, { useState, useEffect } from 'react';
import { Amplify } from 'aws-amplify';
import { Authenticator, useAuthenticator, View } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import awsExports from './aws-exports';
import './App.css';
import './components/MobileResponsive.css';

import Dashboard from './components/Dashboard';
import NewsSourceManager from './components/NewsSourceManager';
import PodcastPlayer from './components/PodcastPlayer';
import ScheduleManager from './components/ScheduleManager';
import './components/LoginLanding.css';
import { restoreAll, isTutorialDone, setTutorialDone, clearTutorialDone } from './utils/dismissals';
import './components/common/appFooter.css';
import { PlayerProvider } from './player/PlayerContext';
import PlayerBar from './player/PlayerBar';
import WelcomeTour from './components/onboarding/WelcomeTour';
import { scheduleService } from './services/scheduleService';
import { usePlan, resetEntitlementForUser } from './hooks/usePlan';
import PlanBadge from './components/billing/PlanBadge';
import PricingPage from './components/billing/PricingPage';
import HelpPage from './components/common/HelpPage';
import { POLLY_STANDARD_VOICE_MAP } from './constants/pollyStandardVoices';
import CapacityLine from './components/billing/CapacityLine';
import { PRICING_MODAL_OPEN_EVENT, openPricingModal } from './utils/pricingModal';
import { useHeartbeat } from './hooks/useHeartbeat';

// Configure Amplify
Amplify.configure(awsExports);

function App() {
  const components = {
    Header() {
      return null; // Logo is already shown in the hero section
    },
    ConfirmSignUp: {
      // Verification emails from a fresh sending domain sometimes land in spam/junk, especially
      // on Outlook/Hotmail — tell users where to look so they don't stall at this step.
      Header() {
        return (
          <View padding="0 0 0.5rem 0">
            <p style={{ fontSize: '0.85rem', color: '#555', margin: 0, textAlign: 'center' }}>
              📬 We emailed you a 6-digit code. If it's not in your inbox within a minute,
              please check your <strong>spam / junk</strong> folder (and mark it "not junk"
              so future emails arrive).
            </p>
          </View>
        );
      },
    },
  };

  const formFields = {
    signIn: {
      username: {
        placeholder: 'Enter your email',
      },
    },
    signUp: {
      email: {
        order: 1,
      },
      password: {
        order: 2,
      },
      confirm_password: {
        order: 3,
      },
    },
  };

  return (
    <Authenticator components={components} formFields={formFields}>
      {({ signOut, user }) => <AuthenticatedApp signOut={signOut} user={user} />}
    </Authenticator>
  );
}

// Split out of App() so first-run tour detection (T7) — which depends on
// `user` — can use hooks safely: `user` only exists inside the
// Authenticator's render-prop callback, and that callback is only invoked
// once `route === 'authenticated'`. Calling hooks directly inside the inline
// callback would call them conditionally across App's renders (a rules-of-
// hooks violation); a real component boundary avoids that.
function AuthenticatedApp({ signOut, user }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showTour, setShowTour] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const { plan, loading: planLoading } = usePlan();

  // Fire-and-forget POST /user/heartbeat on mount and on visibilitychange→visible,
  // client-throttled to once per 6h (§6/§9, free-tier-cap-enforcement plan). This is what
  // actually stamps activity and claims an offered slot server-side (T6) — background polling
  // alone must never count, or the waitlist can never drain.
  useHeartbeat(user?.username);

  // Header "Plan"/"Subscription" button copy — plus/comp get the
  // billing-flavored label, free AND the loading fallback (usePlan resolves
  // loading to plan:'plus' — see usePlan.js — so we must gate on planLoading
  // explicitly here, not just `plan`) get the upsell label. This avoids a
  // flash of "💳 Subscription" for free users while the entitlement fetch is
  // still in flight.
  const isPaidPlan = !planLoading && (plan === 'plus' || plan === 'comp');
  const planButtonEmoji = isPaidPlan ? '💳' : '⭐';
  // "Free plan" (not just "Plan") so free users always see their plan state in the header.
  const planButtonText = isPaidPlan ? 'Subscription' : 'Free plan';

  // FIX 6: the entitlement cache in usePlan is module-global. Reset it whenever the signed-in user
  // changes so a second user in the same tab (login → logout → login) never inherits the previous
  // user's plan. Guarded internally on the sub, so a same-user re-render is a no-op.
  useEffect(() => {
    resetEntitlementForUser(user?.username);
  }, [user?.username]);

  // Any plan-locked hint anywhere in the app (waitlist banner, SourceWizard's
  // locked custom-source card, ScheduleWizard/ScheduleEditor's intro/outro
  // hints) opens this same modal via a window event instead of prop-drilling
  // a callback through every intermediate component — see utils/pricingModal.js.
  useEffect(() => {
    const openModal = () => setPricingOpen(true);
    window.addEventListener(PRICING_MODAL_OPEN_EVENT, openModal);
    return () => window.removeEventListener(PRICING_MODAL_OPEN_EVENT, openModal);
  }, []);

  // T7 first-run detection: show the welcome tour once, only when the user
  // has never finished/skipped it AND has zero sources AND zero schedules
  // (the schedules check grandfathers existing users in automatically).
  // Computed once per mount. Any error, or a non-empty schedules result,
  // means "do not show" — a false negative (existing user briefly not
  // seeing a replay) is fine; a false positive (tour popping up on an
  // established account) is not.
  useEffect(() => {
    let cancelled = false;

    async function checkFirstRun() {
      const sub = user?.username;
      if (isTutorialDone(sub)) return;

      let sourcesCount = 0;
      try {
        sourcesCount = JSON.parse(localStorage.getItem(`sources_${sub}`) || '[]').length;
      } catch (error) {
        // Malformed localStorage — treat as "do not show" (favor false negative).
        console.error('WelcomeTour: failed to read sources for first-run check', error);
        return;
      }
      if (sourcesCount > 0) return;

      try {
        const schedules = await scheduleService.listSchedules();
        if (!cancelled && Array.isArray(schedules) && schedules.length === 0) {
          setShowTour(true);
        }
      } catch (error) {
        // Any error → do not show the tour.
        console.error('WelcomeTour: schedules check failed, not showing tour', error);
      }
    }

    checkFirstRun();
    return () => {
      cancelled = true;
    };
  }, [user?.username]);

  // Any way of leaving the tour (✕ / Skip / Esc / final-slide CTA) persists
  // tutorial_done_${sub} so it never shows again.
  const closeTour = () => {
    setTutorialDone(user?.username);
    setShowTour(false);
  };

  // "Show tutorial" footer link (T7.3): clears the flag and reopens the tour.
  const openTourManually = () => {
    clearTutorialDone(user?.username);
    setShowTour(true);
  };

  return (
    <PlayerProvider>
      <div className="App">
        {showTour && <WelcomeTour onClose={closeTour} />}
        <header className="App-header">
          <div className="header-content">
            <h1>
              <img src="/logo512.png" alt="LinguaNewsCast" style={{ width: '40px', height: '40px', marginRight: '10px', verticalAlign: 'middle' }} />
              LinguaNewsCast
              <PlanBadge />
            </h1>
            <div className="user-info">
              <span className="welcome-text">Welcome, {user?.signInDetails?.loginId || user?.attributes?.email || user?.username}</span>
              <button onClick={openPricingModal} className="plan-header-btn" title="View your plan">
                <span aria-hidden="true">{planButtonEmoji}</span>
                <span className="plan-header-btn__label">{planButtonText}</span>
              </button>
              <button onClick={signOut} className="sign-out-btn" title="Sign Out">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                  <polyline points="16 17 21 12 16 7"></polyline>
                  <line x1="21" y1="12" x2="9" y2="12"></line>
                </svg>
              </button>
            </div>
          </div>
        </header>

        <nav className="nav-tabs">
          <button
            className={activeTab === 'dashboard' ? 'active' : ''}
            onClick={() => setActiveTab('dashboard')}
          >
            🏠 Home
          </button>
          <button
            className={activeTab === 'sources' ? 'active' : ''}
            onClick={() => setActiveTab('sources')}
          >
            News Sources
          </button>
          <button
            className={activeTab === 'schedules' ? 'active' : ''}
            onClick={() => setActiveTab('schedules')}
          >
            Schedules
          </button>
          <button
            className={activeTab === 'podcasts' ? 'active' : ''}
            onClick={() => setActiveTab('podcasts')}
          >
            My Podcasts
          </button>
        </nav>

        <main className="main-content">
          <PlayerBar />
          {activeTab === 'dashboard' && <Dashboard user={user} setActiveTab={setActiveTab} />}
          {activeTab === 'sources' && <NewsSourceManager user={user} setActiveTab={setActiveTab} />}
          {activeTab === 'schedules' && <ScheduleManager user={user} />}
          {activeTab === 'podcasts' && <PodcastPlayer user={user} />}
        </main>

        <footer className="app-footer">
          <button
            type="button"
            className="app-footer__link"
            onClick={() => restoreAll(user?.username)}
          >
            Restore tips
          </button>
          <button
            type="button"
            className="app-footer__link"
            onClick={openTourManually}
          >
            Show tutorial
          </button>
          <button
            type="button"
            className="app-footer__link"
            onClick={() => setHelpOpen(true)}
          >
            Help &amp; FAQ
          </button>
          {plan === 'free' && (
            <button
              type="button"
              className="app-footer__link app-footer__link--upgrade"
              onClick={() => setPricingOpen(true)}
            >
              ⭐ Upgrade to Plus
            </button>
          )}
        </footer>

        {pricingOpen && (
          <PricingPage user={user} onClose={() => setPricingOpen(false)} />
        )}
        {helpOpen && (
          <HelpPage onClose={() => setHelpOpen(false)} />
        )}
      </div>
    </PlayerProvider>
  );
}

// Wrap the Authenticator with custom landing page
export default function AppWithLanding() {
  return (
    <Authenticator.Provider>
      <CustomAuthWrapper>
        <App />
      </CustomAuthWrapper>
    </Authenticator.Provider>
  );
}

// Distinct language names for the landing chips, derived from the real voice map so the
// list can never drift from what the product actually supports. "Spanish (Spain)" and
// "Spanish (Mexico)" collapse to one "Spanish" chip.
const LANDING_LANGUAGES = [...new Set(
  Object.values(POLLY_STANDARD_VOICE_MAP).map((v) => v.languageName.replace(/\s*\(.*\)$/, ''))
)].sort();

function CustomAuthWrapper({ children }) {
  const { route } = useAuthenticator((context) => [context.route]);
  
  // Show custom landing page only when not authenticated
  if (route !== 'authenticated') {
    return (
      <div className="login-landing">
        <div className="login-background"></div>
        <div className="login-container">
          <div className="login-hero">
            <div className="logo-showcase">
              <img 
                src="/logo512.png" 
                alt="LinguaNewsCast" 
                className="hero-logo"
              />
            </div>
            <h1 className="app-title">LinguaNewsCast</h1>
            <p className="app-tagline">
              Your Personal Multilingual News Podcast
            </p>
            <p className="app-explainer">
              Pick your news sources, a language, and a schedule — LinguaNewsCast turns the
              day&apos;s headlines into podcast episodes read aloud in the language you&apos;re
              learning, side by side with English.
            </p>
          </div>

          <div className="login-form-column">
            <CapacityLine />
            <div className="login-form-container" style={{maxWidth: '450px', width: '100%', padding: '0.5rem'}}>
              {children}
            </div>
          </div>

          <div className="landing-lower">
            <div className="feature-highlights">
              <div className="feature">
                <span className="feature-icon">🌍</span>
                <span className="feature-text">
                  <span className="feature-title">Learn languages with real news</span>
                  <span className="feature-sub">
                    Bilingual episodes alternate between English and your target language,
                    sentence by sentence — comprehension in context, not flashcards.
                  </span>
                </span>
              </div>
              <div className="feature">
                <span className="feature-icon">🎙️</span>
                <span className="feature-text">
                  <span className="feature-title">Your news, not a generic feed</span>
                  <span className="feature-sub">
                    Episodes are built from the latest articles of sources you choose —
                    BBC, The Guardian and more.
                  </span>
                </span>
              </div>
              <div className="feature">
                <span className="feature-icon">⏰</span>
                <span className="feature-text">
                  <span className="feature-title">Hands-free, on your schedule</span>
                  <span className="feature-sub">
                    Fresh episodes generate automatically and land in your private feed —
                    listen here or in any podcast app.
                  </span>
                </span>
              </div>
            </div>
            <div className="language-showcase">
              <p className="language-showcase__label">
                Listen in {LANDING_LANGUAGES.length} languages
              </p>
              <div className="language-showcase__chips">
                {LANDING_LANGUAGES.map((name) => (
                  <span className="language-showcase__chip" key={name}>{name}</span>
                ))}
              </div>
            </div>
          </div>

          <footer className="login-footer">
            <p>Stay informed, get fluent. All in one podcast.</p>
          </footer>
        </div>
      </div>
    );
  }
  
  // Show the authenticated app
  return children;
}
